import React, { useEffect, useState } from "react";

// Types
import { TCreateInvitation, TPendingInvitation } from "./InviteUsersModal.types";

// Services
import { setToastConfig } from "store/slice/UI";

// Hooks
import { useAppDispatch } from "app/hooks";

// Utils
import { generateID, isEmailValid } from "utils";
import { sendNewInvitation, sendReminderInvitation } from "services";
import { useIntl } from "react-intl";
import { formatDistanceToNow } from "date-fns";

import icons from "enums/icons";
import { InputErrorMessage } from "components/InputErrorMessage";

const invInitialState = {
    id: -1,
    name: "",
    email: "",
    org_id: 0,
    message: null,
};

const useInviteUsersModal = (
    pendingInvitations: TPendingInvitation[],
    org_id: number | undefined,
    selectedInvitations: number[],
    setSelectedInvitations: React.Dispatch<React.SetStateAction<number[]>>,
    onClose: () => void
): {
    openMessages: Record<number, boolean>;
    anchorEl: { element: HTMLElement | null; id: number | null };
    openSubmenu: boolean;
    invitationsData: TCreateInvitation[];
    filteredInv: TPendingInvitation[];
    areFieldsValid: boolean;
    setAnchorEl: React.Dispatch<React.SetStateAction<{ element: HTMLElement | null; id: number | null }>>;
    setOpenMessages: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
    handleOpenMessage: (index: number) => void;
    handleOnChange: (index: number, field: keyof TCreateInvitation, value: string) => void;
    handleAddInvitations: () => void;
    removeInvitation: (indexToRemove: number) => void;
    handleSend: () => void;
    handleFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleCheckboxChange: (id: number, isChecked: boolean) => void;
    handleSendReminder: (inv_ids?: number[]) => void;
    handleCloseModal: () => void;
    getNameFieldError: (invitation: TCreateInvitation) => React.ReactNode;
    getEmailFieldError: (pendingInvitations: TPendingInvitation[], invitation: TCreateInvitation) => React.ReactNode;
} => {
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [anchorEl, setAnchorEl] = useState<{ element: HTMLElement | null; id: number | null }>({
        element: null,
        id: null,
    });
    const openSubmenu = Boolean(anchorEl.element);

    const [invitationsData, setInvitationsData] = useState<TCreateInvitation[]>([invInitialState]);
    const [filteredInv, setFilteredInv] = useState<TPendingInvitation[]>([]);

    const [openMessages, setOpenMessages] = useState<Record<number, boolean>>({});
    const areFieldsValid = invitationsData.every(
        (invitation) => invitation.name.trim().length >= 2 && invitation.email.trim() !== ""
    );

    useEffect(() => {
        setFilteredInv(pendingInvitations);
    }, [pendingInvitations]);

    const handleOpenMessage = (id: number) => {
        setOpenMessages((prevOpenMessages) => ({
            ...prevOpenMessages,
            [id]: !prevOpenMessages[id],
        }));
    };

    const handleOnChange = (id: number, field: keyof TCreateInvitation, value: string) => {
        setInvitationsData((prevInv) =>
            prevInv.map((invitation) => (invitation.id === id ? { ...invitation, [field]: value } : invitation))
        );
    };

    const handleAddInvitations = () => {
        const newId = generateID();
        setInvitationsData((prevInv) => {
            const newInvitation = { ...invInitialState, id: newId };

            return [...prevInv, newInvitation];
        });

        setOpenMessages((prevOpenMessages) => ({
            ...prevOpenMessages,
            [newId]: false,
        }));
    };

    const removeInvitation = (idToRemove: number) => {
        setInvitationsData((prevInvitations) => prevInvitations.filter((invitation) => invitation.id !== idToRemove));

        setOpenMessages((prevOpenMessages) => {
            const updatedMessages = { ...prevOpenMessages };
            delete updatedMessages[idToRemove];
            return updatedMessages;
        });
    };

    const handleCheckboxChange = (id: number, isChecked: boolean) => {
        setSelectedInvitations((prev) => (isChecked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)));
    };

    const handleSend = async () => {
        const invites = invitationsData.map((invitation) => ({
            name: invitation.name,
            email: invitation.email,
            org_id,
            message: invitation.message || null,
        }));

        try {
            const response = await sendNewInvitation({ invites }).catch((error) => error.response);

            if (response && response.status === 207) {
                setInvitationsData((prevInvitations) =>
                    prevInvitations.map((inv) => {
                        const failedItem = response.data.detail.report.find(
                            (item: any) => item.recipient_email === inv.email
                        );

                        return failedItem
                            ? {
                                  ...inv,
                                  errorData: failedItem,
                              }
                            : inv;
                    })
                );
            }

            if (response && response.status === 200) {
                const newPendingInvitations: TPendingInvitation[] = invites.map((invite) => ({
                    id: generateID(),
                    sent_utc: new Date().toISOString(),
                    recipient_email: invite.email,
                    recipient_name: invite.name,
                }));

                setFilteredInv((prevFilteredInv) => [...newPendingInvitations, ...prevFilteredInv]);

                if (typeof pendingInvitations !== "undefined") {
                    pendingInvitations.unshift(...newPendingInvitations);
                }

                setInvitationsData([invInitialState]);

                dispatch(
                    setToastConfig({
                        title: "User invite sent",
                        content: "To manage pending invitations, check the link above.",
                    })
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleSendReminder = async (inv_ids?: number[]) => {
        if (inv_ids && inv_ids.length > 0) {
            const invitesToResend = inv_ids.map((id) => ({ invite_id: id }));
            try {
                await sendReminderInvitation({ invite_list: invitesToResend });
                setSelectedInvitations((prev) => prev.filter((id) => !inv_ids.includes(id)));
                dispatch(
                    setToastConfig({
                        title: "User invite sent",
                        content: "To manage pending invitations, check the link above.",
                    })
                );
            } catch (error) {
                console.error(error);
            }
            return;
        }
        const errorInvites = invitationsData.filter((inv) => inv.errorData?.status === "INVITE_ALREADY_EXISTS");

        if (errorInvites.length > 0) {
            const invitesToResend = errorInvites.map((inv) => ({
                invite_id: inv.errorData?.invite_id,
            }));

            try {
                await sendReminderInvitation({ invite_list: invitesToResend });

                setSelectedInvitations([]);
                setInvitationsData([invInitialState]);
                dispatch(
                    setToastConfig({
                        title: "User invite sent",
                        content: "To manage pending invitations, check the link above.",
                    })
                );
            } catch (error) {
                console.error(error);
            }
            return;
        }

        const invitesToResend = filteredInv
            .filter((inv) => selectedInvitations.includes(inv.id))
            .map((inv) => ({
                invite_id: inv.id,
            }));

        try {
            await sendReminderInvitation({ invite_list: invitesToResend });
            setSelectedInvitations([]);
        } catch (error) {
            console.error(error);
        }
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value.toLowerCase();
        setFilteredInv(
            pendingInvitations.filter(
                (user) =>
                    user.recipient_name.toLowerCase().includes(text) ||
                    user.recipient_email.toLowerCase().includes(text)
            )
        );
    };

    const handleCloseModal = () => {
        setInvitationsData([invInitialState]);
        onClose();
    };

    const getNameFieldError = (invitation: TCreateInvitation) => {
        // Name required
        if (!invitation.name.trim()) {
            return <InputErrorMessage message="*Required" />;
        }

        // Check name length
        if (invitation.name.trim().length < 2) {
            return (
                <InputErrorMessage
                    icon={icons.errorTriangle}
                    message={intl.formatMessage({ id: "hubManagement.client.addUsers.inviteUser.invalidNameMessage" })}
                />
            );
        }
        return "";
    };

    const getEmailFieldError = (
        pendingInvitations: TPendingInvitation[],
        invitation: TCreateInvitation
    ): React.ReactNode => {
        // Email required
        if (!invitation.email.trim()) {
            return <InputErrorMessage message="*Required" />;
        }

        // Check email format
        if (!isEmailValid(invitation.email)) {
            return (
                <InputErrorMessage
                    icon={icons.errorTriangle}
                    message={intl.formatMessage({ id: "hubManagement.client.addUsers.inviteUser.invalidEmailMessage" })}
                />
            );
        }

        // Email exists on pending invitations
        const pending = pendingInvitations.find(
            (item) => item.recipient_email.toLowerCase() === invitation.email.toLocaleLowerCase()
        );

        if (pending) {
            if (!pending.sent_utc) return;

            const daysAgo = formatDistanceToNow(new Date(pending.sent_utc), { addSuffix: true });

            return (
                <InputErrorMessage
                    icon={icons.errorTriangle}
                    message={intl.formatMessage(
                        { id: "hubManagement.client.addUsers.inviteUser.warningEmailMessage" },
                        { daysAgo: daysAgo }
                    )}
                />
            );
        }

        // Errors returned by API response
        if (
            invitation.errorData?.readable_error_segments &&
            invitation.errorData?.readable_error_segments?.length > 0
        ) {
            return (
                <InputErrorMessage
                    icon={icons.errorTriangle}
                    message={
                        invitation.errorData?.readable_error_segments
                            ? invitation.errorData?.readable_error_segments.map((segment, index) => (
                                  <span
                                      key={index}
                                      style={
                                          segment.style === "underline"
                                              ? {
                                                    textDecoration: "underline",
                                                }
                                              : {}
                                      }
                                  >
                                      {segment.segment}
                                  </span>
                              ))
                            : invitation.message
                    }
                />
            );
        }

        return "";
    };

    return {
        openMessages,
        anchorEl,
        openSubmenu,
        invitationsData,
        filteredInv,
        areFieldsValid,
        setAnchorEl,
        setOpenMessages,
        handleOpenMessage,
        handleOnChange,
        handleAddInvitations,
        removeInvitation,
        handleSend,
        handleFilterChange,
        handleCheckboxChange,
        handleSendReminder,
        handleCloseModal,
        getNameFieldError,
        getEmailFieldError,
    };
};

export default useInviteUsersModal;
